import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { useNotification } from '@/components/NotificationProvider';
import { usePermissions } from '@/components/PermissionsProvider';
import { Plus, MoreHorizontal, Pencil, Trash2, Upload, Download, ClipboardCheck, Shield, Package, DollarSign, Ruler, Search, X, Tag, History, AlertTriangle, RotateCcw, ArrowLeft } from 'lucide-react';
import { formatDateForInput, formatDisplayDate } from '@/utils/dateUtils';
import { AuditTrailPanel, LastModifiedBadge } from '@/components/AuditTrailPanel';
import { useTableView, TableViewToolbar, ColumnFilterRow, SortableHead } from '@/components/TableViewManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getAuthHeader() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

const OWNERSHIP_CURRENCY_MAP = { 'Fagioli SPA': 'EUR', 'Fagioli Australia': 'AUD', 'Fagioli Indonesia': 'IDR', 'Fagioli Polska': 'PLN' };
const CURRENCIES = ['EUR', 'AUD', 'USD', 'IDR', 'PLN', 'GBP'];
const DEPRECIATION_METHODS = ['Straight Line', 'Reducing Balance', 'Units of Production'];
const INSURANCE_POLICY_TYPES = ['Public Liability', 'Equipment/Plant', 'Transit/Marine', 'Professional Indemnity', 'Workers Comp', 'Third Party'];

// Default formData shape
const DEFAULT_FORM = {
  // General
  category: '', subcategory: '', equipment_name: '', aka: '', bundle_type: '', included_in: '', included_in_label: '',
  manufacturer: '', model: '', year_of_manufacture: '', internal_tag: '', capacity: '', serial_number: '',
  alevro_tag: '', jv_code: '', quantity: '1', assignment: '', location: '', maintenance_status: 'Operational', next_maintenance_date: '',
  // Dimensions & Shipping
  weight_kg: '', width_cm: '', length_cm: '', height_cm: '', unit_weight_kg: '',
  hs_code: '', customs_regime: '', customs_expiry_date: '', drawing_number: '', shipping_notes: '',
  // Finance & Accounting
  ownership: '', currency: '', asset_book: false, asset_book_code: '', acquisition_cost: '', acquisition_date: '',
  depreciation_method: '', useful_life_years: '', market_value: '', insured_value: '',
  applicable_insurance_policies: [], standard_daily_cost: '', release_quarter: '', intercompany_daily_rate: '',
  disposal_date: '', finance_notes: '',
  // Sales & Tendering
  pricing_matrix: { emea_short: '', emea_mid: '', emea_long: '', apac_short: '', apac_mid: '', apac_long: '', na_short: '', na_mid: '', na_long: '' },
  sales_notes: ''
};

export default function Equipment({ user }) {
  const notification = useNotification();
  const [equipment, setEquipment] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [bundleTypes, setBundleTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [ownershipEntities, setOwnershipEntities] = useState([]);
  const [open, setOpen] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [formData, setFormData] = useState({ ...DEFAULT_FORM });

  // Search & Filters
  const [searchQuery, setSearchQuery] = useState(() => new URLSearchParams(window.location.search).get('q') || '');
  // Server-side pagination ("load more") state
  const PAGE_SIZE = 50;
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [equipmentOptions, setEquipmentOptions] = useState([]);
  const reloadRef = useRef(() => {});
  const sentinelRef = useRef(null);

  // Inspections
  const [inspections, setInspections] = useState([]);
  const [inspFormData, setInspFormData] = useState({ id: '', type: '', name: '', inspector: '', certificate_number: '', inspection_date: '', next_due_date: '', result: 'Pass', swl_verified: '', notes: '' });
  const [editingInsp, setEditingInsp] = useState(null);
  const [showInspForm, setShowInspForm] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);

  // Bulk select & trash (mirror Personnel page)
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showTrash, setShowTrash] = useState(false);
  const [trashedEquipment, setTrashedEquipment] = useState([]);

  useEffect(() => {
    fetchCategories();
    fetchProjects();
    fetchMasterData();
    fetchTrashedEquipment();
    fetchEquipmentOptions();
  }, []);

  const fetchEquipmentOptions = async () => {
    try {
      const res = await axios.get(`${API}/equipment/options`, { headers: getAuthHeader() });
      setEquipmentOptions(res.data);
    } catch (error) { /* Silently fail */ }
  };

  const fetchCategories = async () => {
    try {
      const [categoriesRes, subcategoriesRes] = await Promise.all([
        axios.get(`${API}/categories?type=equipment_category`, { headers: getAuthHeader() }),
        axios.get(`${API}/categories?type=equipment_subcategory`, { headers: getAuthHeader() })
      ]);
      setCategories(categoriesRes.data);
      setSubcategories(subcategoriesRes.data);
    } catch (error) { /* Silently fail */ }
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/projects`, { headers: getAuthHeader() });
      setProjects(res.data);
    } catch (error) { /* Silently fail */ }
  };

  const fetchMasterData = async () => {
    try {
      const [btRes, locRes, ownRes] = await Promise.all([
        axios.get(`${API}/categories?type=bundle_type`, { headers: getAuthHeader() }),
        axios.get(`${API}/categories?type=location`, { headers: getAuthHeader() }),
        axios.get(`${API}/categories?type=ownership_entity`, { headers: getAuthHeader() })
      ]);
      setBundleTypes(btRes.data.map(c => c.name));
      setLocations(locRes.data.map(c => c.name));
      setOwnershipEntities(ownRes.data.map(c => c.name));
    } catch (error) { /* Silently fail */ }
  };

  // Reload page 1 with current filters (used by mutation handlers after create/edit/delete/import).
  const fetchEquipment = useCallback(() => reloadRef.current(), []);

  // Self-calculating fields
  const calcVolume = useMemo(() => {
    const w = parseFloat(formData.width_cm) || 0;
    const l = parseFloat(formData.length_cm) || 0;
    const h = parseFloat(formData.height_cm) || 0;
    if (w && l && h) return ((w * l * h) / 1000000).toFixed(4);
    return '';
  }, [formData.width_cm, formData.length_cm, formData.height_cm]);

  const calcTotalWeight = useMemo(() => {
    const uw = parseFloat(formData.unit_weight_kg) || 0;
    const qty = parseInt(formData.quantity) || 1;
    if (uw) return (uw * qty).toFixed(2);
    return '';
  }, [formData.unit_weight_kg, formData.quantity]);

  const calcFreightTons = useMemo(() => {
    const tw = parseFloat(calcTotalWeight) || 0;
    const vol = parseFloat(calcVolume) || 0;
    if (tw || vol) return Math.max(tw / 1000, vol).toFixed(4);
    return '';
  }, [calcTotalWeight, calcVolume]);

  const calcYtdDepreciation = useMemo(() => {
    const cost = parseFloat(formData.acquisition_cost) || 0;
    const life = parseInt(formData.useful_life_years) || 0;
    const acqDate = formData.acquisition_date ? new Date(formData.acquisition_date) : null;
    if (!cost || !life || !acqDate || formData.depreciation_method !== 'Straight Line') return '';
    const now = new Date();
    const yearStart = new Date(now.getFullYear(), 0, 1);
    const startDate = acqDate > yearStart ? acqDate : yearStart;
    const monthsThisYear = Math.max(0, (now.getMonth() - startDate.getMonth() + 1));
    const annualDep = cost / life;
    return ((annualDep / 12) * monthsThisYear).toFixed(2);
  }, [formData.acquisition_cost, formData.useful_life_years, formData.acquisition_date, formData.depreciation_method]);

  const calcNetBookValue = useMemo(() => {
    const cost = parseFloat(formData.acquisition_cost) || 0;
    const life = parseInt(formData.useful_life_years) || 0;
    const acqDate = formData.acquisition_date ? new Date(formData.acquisition_date) : null;
    if (!cost || !life || !acqDate) return '';
    const now = new Date();
    const yearsElapsed = (now - acqDate) / (365.25 * 24 * 60 * 60 * 1000);
    if (formData.depreciation_method === 'Straight Line') {
      const totalDep = Math.min(cost, (cost / life) * yearsElapsed);
      return Math.max(0, cost - totalDep).toFixed(2);
    }
    return '';
  }, [formData.acquisition_cost, formData.useful_life_years, formData.acquisition_date, formData.depreciation_method]);

  // "Included In" options from the full (slim) equipment options list
  const includedInOptions = useMemo(() => {
    return equipmentOptions
      .filter(e => editingItem ? e.id !== editingItem.id : true)
      .map(e => ({ id: e.id, label: `${e.equipment_name || e.category} - ${e.internal_tag}` }));
  }, [equipmentOptions, editingItem]);

  const parseNum = (val) => val !== '' && val !== null && val !== undefined ? parseFloat(val) : null;
  const parseInt_ = (val) => val !== '' && val !== null && val !== undefined ? parseInt(val) : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        category: formData.category,
        subcategory: formData.subcategory,
        equipment_name: formData.equipment_name || null,
        aka: formData.aka || null,
        bundle_type: formData.bundle_type || null,
        included_in: formData.included_in || null,
        included_in_label: formData.included_in_label || null,
        manufacturer: formData.manufacturer || null,
        model: formData.model || null,
        year_of_manufacture: parseInt_(formData.year_of_manufacture),
        internal_tag: formData.internal_tag,
        capacity: formData.capacity || null,
        serial_number: formData.serial_number,
        alevro_tag: formData.alevro_tag || null,
        jv_code: formData.jv_code || null,
        quantity: parseInt(formData.quantity) || 1,
        assignment: formData.assignment || null,
        location: formData.location,
        maintenance_status: formData.maintenance_status,
        next_maintenance_date: formData.next_maintenance_date ? new Date(formData.next_maintenance_date).toISOString() : null,
        // Dimensions
        weight_kg: parseNum(formData.weight_kg),
        width_cm: parseNum(formData.width_cm),
        length_cm: parseNum(formData.length_cm),
        height_cm: parseNum(formData.height_cm),
        unit_weight_kg: parseNum(formData.unit_weight_kg),
        hs_code: formData.hs_code || null,
        customs_regime: formData.customs_regime || null,
        customs_expiry_date: formData.customs_expiry_date ? new Date(formData.customs_expiry_date).toISOString() : null,
        drawing_number: formData.drawing_number || null,
        shipping_notes: formData.shipping_notes || null,
        // Finance
        ownership: formData.ownership || null,
        currency: formData.currency || null,
        asset_book: formData.asset_book || false,
        asset_book_code: formData.asset_book ? (formData.asset_book_code || null) : null,
        acquisition_cost: parseNum(formData.acquisition_cost),
        acquisition_date: formData.acquisition_date ? new Date(formData.acquisition_date).toISOString() : null,
        depreciation_method: formData.depreciation_method || null,
        useful_life_years: parseInt_(formData.useful_life_years),
        market_value: parseNum(formData.market_value),
        insured_value: parseNum(formData.insured_value),
        applicable_insurance_policies: formData.applicable_insurance_policies || [],
        standard_daily_cost: parseNum(formData.standard_daily_cost),
        release_quarter: formData.release_quarter || null,
        intercompany_daily_rate: parseNum(formData.intercompany_daily_rate),
        disposal_date: formData.disposal_date ? new Date(formData.disposal_date).toISOString() : null,
        finance_notes: formData.finance_notes || null,
        // Sales & Tendering
        pricing_matrix: (() => {
          const pm = formData.pricing_matrix || {};
          const hasValues = Object.values(pm).some(v => v !== '' && v !== null && v !== undefined);
          if (!hasValues) return null;
          const out = {};
          for (const [k, v] of Object.entries(pm)) out[k] = v !== '' ? parseFloat(v) : null;
          return out;
        })(),
        sales_notes: formData.sales_notes || null,
        // Inspections
        inspections: inspections.map(i => ({
          id: i.id || undefined, type: i.type, name: i.name, inspector: i.inspector || null,
          certificate_number: i.certificate_number || null,
          inspection_date: i.inspection_date ? new Date(i.inspection_date).toISOString() : null,
          next_due_date: i.next_due_date ? new Date(i.next_due_date).toISOString() : null,
          result: i.result || 'Pass', swl_verified: i.swl_verified || null, notes: i.notes || null
        })),
        insurance_records: [],
        version: editingItem?.version || null
      };

      if (editingItem) {
        await axios.put(`${API}/equipment/${editingItem.id}`, data, { headers: getAuthHeader() });
        notification.success('Equipment updated successfully');
      } else {
        await axios.post(`${API}/equipment`, data, { headers: getAuthHeader() });
        notification.success('Equipment created successfully');
      }
      setOpen(false);
      setEditingItem(null);
      resetForm();
      fetchEquipment();
    } catch (error) {
      if (error.response?.status === 409) {
        notification.conflict(() => { setOpen(false); setEditingItem(null); resetForm(); fetchEquipment(); });
      } else {
        notification.error(error.response?.data?.detail || 'Operation failed');
      }
    }
  };

  const handleDelete = (id) => {
    notification.confirm('Move this equipment to trash?\n\nIt can be restored from the Trash view.', async () => {
      try {
        await axios.delete(`${API}/equipment/${id}`, { headers: getAuthHeader() });
        notification.success('Equipment moved to trash');
        fetchEquipment();
        fetchTrashedEquipment();
      } catch (error) { notification.error('Failed to delete equipment'); }
    });
  };

  // ---- Bulk select & trash management (mirrors Personnel) ----
  const fetchTrashedEquipment = async () => {
    try {
      const res = await axios.get(`${API}/equipment/trash/list`, { headers: getAuthHeader() });
      setTrashedEquipment(res.data);
    } catch (error) { /* silently fail */ }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredEquipment.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredEquipment.map(e => e.id)));
    }
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    notification.confirm(
      `Move ${selectedIds.size} equipment item${selectedIds.size > 1 ? 's' : ''} to trash?\n\nThey can be restored from the Trash view.`,
      async () => {
        try {
          await axios.post(`${API}/equipment/bulk-delete`, { ids: Array.from(selectedIds) }, { headers: getAuthHeader() });
          notification.success(`${selectedIds.size} equipment moved to trash`);
          setSelectedIds(new Set());
          fetchEquipment();
          fetchTrashedEquipment();
        } catch (error) { notification.error('Failed to delete equipment'); }
      }
    );
  };

  const handleRestore = (id) => {
    notification.confirm('Restore this equipment?', async () => {
      try {
        await axios.post(`${API}/equipment/${id}/restore`, {}, { headers: getAuthHeader() });
        notification.success('Equipment restored');
        fetchEquipment();
        fetchTrashedEquipment();
      } catch (error) { notification.error('Failed to restore'); }
    }, { title: 'Restore Equipment', confirmLabel: 'Restore', confirmClass: 'bg-emerald-600 hover:bg-emerald-700' });
  };

  const handlePermanentDelete = (id) => {
    notification.confirm(
      'Permanently delete this equipment?\n\nThis CANNOT be undone.',
      async () => {
        try {
          await axios.delete(`${API}/equipment/${id}/permanent`, { headers: getAuthHeader() });
          notification.success('Equipment permanently deleted');
          fetchTrashedEquipment();
        } catch (error) { notification.error('Failed to delete'); }
      },
      { title: 'Permanent Delete', confirmLabel: 'Delete Permanently', confirmClass: 'bg-red-600 hover:bg-red-700' }
    );
  };

  const handleEmptyTrash = () => {
    if (trashedEquipment.length === 0) return;
    notification.confirm(
      `Permanently delete all ${trashedEquipment.length} item${trashedEquipment.length > 1 ? 's' : ''} in trash?\n\nThis CANNOT be undone.`,
      async () => {
        try {
          await axios.post(`${API}/equipment/trash/empty`, {}, { headers: getAuthHeader() });
          notification.success('Trash emptied');
          fetchTrashedEquipment();
        } catch (error) { notification.error('Failed to empty trash'); }
      },
      { title: 'Empty Trash', confirmLabel: 'Empty Trash', confirmClass: 'bg-red-600 hover:bg-red-700' }
    );
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      category: item.category || '', subcategory: item.subcategory || '',
      equipment_name: item.equipment_name || '', aka: item.aka || '',
      bundle_type: item.bundle_type || '', included_in: item.included_in || '', included_in_label: item.included_in_label || '',
      manufacturer: item.manufacturer || '', model: item.model || '',
      year_of_manufacture: item.year_of_manufacture?.toString() || '',
      internal_tag: item.internal_tag || '', capacity: item.capacity || '',
      serial_number: item.serial_number || '', alevro_tag: item.alevro_tag || '',
      jv_code: item.jv_code || '', quantity: item.quantity?.toString() || '1',
      assignment: item.assignment || '', location: item.location || '',
      maintenance_status: item.maintenance_status || 'Operational',
      next_maintenance_date: formatDateForInput(item.next_maintenance_date),
      weight_kg: item.weight_kg?.toString() || '', width_cm: item.width_cm?.toString() || '',
      length_cm: item.length_cm?.toString() || '', height_cm: item.height_cm?.toString() || '',
      unit_weight_kg: item.unit_weight_kg?.toString() || '',
      hs_code: item.hs_code || '', customs_regime: item.customs_regime || '',
      customs_expiry_date: formatDateForInput(item.customs_expiry_date),
      drawing_number: item.drawing_number || '', shipping_notes: item.shipping_notes || '',
      ownership: item.ownership || '', currency: item.currency || '',
      asset_book: item.asset_book || false, asset_book_code: item.asset_book_code || '',
      acquisition_cost: item.acquisition_cost?.toString() || '',
      acquisition_date: formatDateForInput(item.acquisition_date),
      depreciation_method: item.depreciation_method || '',
      useful_life_years: item.useful_life_years?.toString() || '',
      market_value: item.market_value?.toString() || '', insured_value: item.insured_value?.toString() || '',
      applicable_insurance_policies: item.applicable_insurance_policies || [],
      standard_daily_cost: (item.standard_daily_cost || item.standard_rate)?.toString() || '',
      release_quarter: item.release_quarter || '',
      intercompany_daily_rate: item.intercompany_daily_rate?.toString() || '',
      disposal_date: formatDateForInput(item.disposal_date),
      finance_notes: item.finance_notes || '',
      pricing_matrix: (() => {
        const pm = item.pricing_matrix || {};
        return {
          emea_short: pm.emea_short?.toString() || '', emea_mid: pm.emea_mid?.toString() || '', emea_long: pm.emea_long?.toString() || '',
          apac_short: pm.apac_short?.toString() || '', apac_mid: pm.apac_mid?.toString() || '', apac_long: pm.apac_long?.toString() || '',
          na_short: pm.na_short?.toString() || '', na_mid: pm.na_mid?.toString() || '', na_long: pm.na_long?.toString() || ''
        };
      })(),
      sales_notes: item.sales_notes || ''
    });
    setInspections((item.inspections || []).map(i => ({
      id: i.id, type: i.type || '', name: i.name || '', inspector: i.inspector || '',
      certificate_number: i.certificate_number || '', inspection_date: formatDateForInput(i.inspection_date),
      next_due_date: formatDateForInput(i.next_due_date), result: i.result || 'Pass',
      swl_verified: i.swl_verified || '', notes: i.notes || ''
    })));
    setActiveTab('general');
    setOpen(true);
  };

  const handleRowClick = (item, e) => {
    if (e.target.closest('[data-actions-menu]')) return;
    handleEdit(item);
  };

  const resetForm = () => {
    setFormData({ ...DEFAULT_FORM });
    setInspections([]);
    setInspFormData({ id: '', type: '', name: '', inspector: '', certificate_number: '', inspection_date: '', next_due_date: '', result: 'Pass', swl_verified: '', notes: '' });
    setEditingInsp(null);
    setShowInspForm(false);
    setActiveTab('general');
  };

  // Inspection handlers
  const handleAddInsp = () => { setEditingInsp(null); setInspFormData({ id: '', type: '', name: '', inspector: '', certificate_number: '', inspection_date: '', next_due_date: '', result: 'Pass', swl_verified: '', notes: '' }); setShowInspForm(true); };
  const handleEditInsp = (insp) => { setEditingInsp(insp); setInspFormData({ ...insp }); setShowInspForm(true); };
  const handleDeleteInsp = (inspId) => { setInspections(inspections.filter(i => i.id !== inspId)); };
  const handleSaveInsp = () => {
    if (!inspFormData.type || !inspFormData.name) { notification.error('Type and Name are required'); return; }
    if (editingInsp) { setInspections(inspections.map(i => i.id === editingInsp.id ? { ...inspFormData } : i)); }
    else { setInspections([...inspections, { ...inspFormData, id: `temp-${Date.now()}` }]); }
    setShowInspForm(false); setEditingInsp(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['.csv', '.xls', '.xlsx'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      if (!validTypes.includes(fileExtension)) { notification.error('Please select a CSV or Excel file'); e.target.value = ''; return; }
      setSelectedFile(file);
    }
  };

  const handleBulkImport = async () => {
    if (!selectedFile) { notification.error('Please select a file'); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', selectedFile);
    try {
      const response = await axios.post(`${API}/equipment/bulk-import`, fd, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
      const { success_count, error_count } = response.data;
      if (error_count > 0) { notification.warning(`Imported ${success_count} items with ${error_count} errors.`); }
      else { notification.success(`Successfully imported ${success_count} equipment items`); }
      setBulkImportOpen(false); setSelectedFile(null); fetchEquipment();
    } catch (error) { notification.error(error.response?.data?.detail || 'Failed to import equipment'); }
    finally { setUploading(false); }
  };

  const downloadTemplate = () => {
    // Header row covers every importable scalar field on the Equipment model.
    // Required: serial_number, internal_tag, category, subcategory, maintenance_status, location.
    // Lists (e.g. applicable_insurance_policies) accept "Item A | Item B" or "Item A, Item B".
    // Booleans accept true/false/yes/no/1/0. Dates accept any ISO/spreadsheet date.
    const headers = [
      // Required
      'serial_number', 'internal_tag', 'category', 'subcategory', 'maintenance_status', 'location',
      // General
      'equipment_name', 'aka', 'bundle_type', 'included_in', 'included_in_label',
      'manufacturer', 'model', 'year_of_manufacture', 'capacity', 'alevro_tag', 'jv_code',
      'quantity', 'assignment', 'next_maintenance_date',
      // Dimensions & Shipping
      'weight_kg', 'width_cm', 'length_cm', 'height_cm', 'unit_weight_kg',
      'hs_code', 'customs_regime', 'customs_expiry_date', 'drawing_number', 'shipping_notes', 'dimensions',
      // Finance
      'ownership', 'currency', 'asset_book', 'asset_book_code',
      'acquisition_cost', 'acquisition_date', 'depreciation_method', 'useful_life_years',
      'market_value', 'insured_value', 'applicable_insurance_policies',
      'standard_daily_cost', 'release_quarter', 'intercompany_daily_rate',
      'disposal_date', 'finance_notes', 'book_value', 'standard_rate',
      // Sales
      'sales_notes',
    ];
    const sample = [
      'EQ-001', 'EXC-01', 'Heavy Machinery', 'Excavator', 'Operational', 'Australia',
      'Sample Excavator 30T', 'Yellow Beast', 'Loose', '', '',
      'Caterpillar', '320GC', '2022', '30 tonne', 'FAG-001', '',
      '1', 'Pluto Train 2', '2026-09-30',
      '30000', '320', '950', '300', '30000',
      '8429.52', 'permanent', '', 'DRW-EXC-01', 'Lift via designated points', '320 x 950 x 300 cm',
      'Fagioli Australia', 'AUD', 'true', 'AB-EXC-01',
      '450000', '2022-04-01', 'Straight Line', '10',
      '420000', '500000', 'Policy A | Policy B',
      '850', 'Q3-2025', '700',
      '', 'In rotation - PEM 30 due', '380000', '850',
      'Premium tier - quote 15% margin'
    ];
    // CSV-escape any field that contains a comma, quote, or newline
    const csvEscape = (v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csvContent = headers.map(csvEscape).join(',') + '\n' + sample.map(csvEscape).join(',');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'equipment_import_template.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
  };

  const getStatusBadge = (status) => {
    const variants = {
      'Operational': 'bg-emerald-50 text-emerald-700 border-emerald-200',
      'Maintenance': 'bg-yellow-50 text-yellow-700 border-yellow-200',
      'Out of Service': 'bg-red-50 text-red-700 border-red-200',
      'Pass': 'bg-emerald-50 text-emerald-700 border-emerald-200',
      'Fail': 'bg-red-50 text-red-700 border-red-200',
      'Conditional': 'bg-yellow-50 text-yellow-700 border-yellow-200'
    };
    return variants[status] || variants.Operational;
  };

  const { canEdit: canEditModule, canView } = usePermissions();
  const canEdit = canEditModule('equipment_database');

  // Filter options from master data (Data Management)
  const filterCategoryOptions = useMemo(() => categories.map(c => c.name).sort(), [categories]);
  const filterSubcategoryOptions = useMemo(() => subcategories.map(s => s.name).sort(), [subcategories]);
  const filterBundleTypeOptions = useMemo(() => bundleTypes.sort(), [bundleTypes]);
  const locationOptions = useMemo(() => [...locations].sort(), [locations]);
  const ownershipOptions = useMemo(() => [...ownershipEntities].sort(), [ownershipEntities]);
  const statusOptions = useMemo(() => ['Operational', 'In Maintenance', 'Out of Service', 'Retired'], []);

  // "Location" is shown as "Site Zone" for the civil/construction tenant (where it tracks
  // on-site zones); all other tenants keep "Location" unchanged.
  const locationLabel = useMemo(() => {
    try {
      const t = JSON.parse(localStorage.getItem('tenant') || 'null') || {};
      return t.client_id === 'besix' ? 'Site Zone' : 'Location';
    } catch { return 'Location'; }
  }, []);

  // Column definitions for TableViewManager
  const tableColumns = useMemo(() => [
    { key: 'internal_tag', label: 'Tag', filterable: true, filterType: 'text' },
    { key: 'name_sn', label: 'Name / S/N', filterable: true, filterType: 'text', accessor: (item) => `${item.equipment_name || ''} ${item.serial_number || ''}` },
    { key: 'category', label: 'Category', filterable: true, filterType: 'select', filterOptions: filterCategoryOptions },
    { key: 'subcategory', label: 'Subcategory', filterable: true, filterType: 'select', filterOptions: filterSubcategoryOptions },
    { key: 'maintenance_status', label: 'Status', filterable: true, filterType: 'select', filterOptions: statusOptions },
    { key: 'location', label: locationLabel, filterable: true, filterType: 'select', filterOptions: locationOptions },
    { key: 'ownership', label: 'Owner', filterable: true, filterType: 'select', filterOptions: ownershipOptions },
    { key: 'bundle_type', label: 'Bundle Type', filterable: true, filterType: 'select', filterOptions: filterBundleTypeOptions },
    { key: 'assignment', label: 'Assignment', filterable: true, filterType: 'text' },
    { key: 'modified_by', label: 'Modified By', filterable: false },
    { key: 'actions', label: 'Actions', filterable: false },
  ], [filterCategoryOptions, filterSubcategoryOptions, filterBundleTypeOptions, locationOptions, ownershipOptions, statusOptions, locationLabel]);

  // Column definitions drive the tableView (column filters, saved views, visibility).
  const tableView = useTableView({ pageKey: 'equipment', columns: tableColumns, data: equipment });
  const filteredEquipment = tableView.filteredData;

  // Map tableView column-filter keys -> server query params
  const columnFilterKey = JSON.stringify(tableView.columnFilters);
  const buildListParams = useCallback((skip) => {
    const p = new URLSearchParams();
    p.set('skip', String(skip));
    p.set('limit', String(PAGE_SIZE));
    if (searchQuery.trim()) p.set('search', searchQuery.trim());
    const cf = tableView.columnFilters || {};
    const map = {
      name_sn: 'name', internal_tag: 'internal_tag', assignment: 'assignment',
      category: 'category', subcategory: 'subcategory', maintenance_status: 'maintenance_status',
      location: 'location', ownership: 'ownership', bundle_type: 'bundle_type',
    };
    Object.entries(cf).forEach(([k, v]) => { if (v && map[k]) p.set(map[k], v); });
    if (tableView.sortBy) { p.set('sort_by', tableView.sortBy); p.set('sort_dir', tableView.sortDir); }
    return p.toString();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, columnFilterKey, tableView.sortBy, tableView.sortDir]);

  const loadFirstPage = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await axios.get(`${API}/equipment/list?${buildListParams(0)}`, { headers: getAuthHeader() });
      setEquipment(res.data.items);
      setTotalCount(res.data.total);
      setHasMore(res.data.has_more);
    } catch (error) {
      notification.error('Failed to load equipment');
    }
    setListLoading(false);
  }, [buildListParams]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const res = await axios.get(`${API}/equipment/list?${buildListParams(equipment.length)}`, { headers: getAuthHeader() });
      setEquipment(prev => [...prev, ...res.data.items]);
      setTotalCount(res.data.total);
      setHasMore(res.data.has_more);
    } catch (error) {
      notification.error('Failed to load more equipment');
    }
    setLoadingMore(false);
  }, [buildListParams, equipment.length, hasMore, loadingMore]);

  useEffect(() => { reloadRef.current = loadFirstPage; }, [loadFirstPage]);

  // Debounced reload whenever search text or column filters change (and on mount)
  useEffect(() => {
    const t = setTimeout(() => { loadFirstPage(); }, 300);
    return () => clearTimeout(t);
  }, [loadFirstPage]);

  // Infinite scroll: auto-load next page when the sentinel scrolls into view
  useEffect(() => {
    if (!hasMore || showTrash) return;
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadMore();
    }, { rootMargin: '250px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loadMore, showTrash]);

  const hasActiveFilters = searchQuery || tableView.activeFilterCount > 0;
  const clearFilters = () => { setSearchQuery(''); tableView.clearAllFilters(); };

  const getComplianceSummary = (item) => {
    const insps = (item.inspections || []).length;
    return { insps, total: insps };
  };

  const handleOwnershipChange = (val) => {
    const autoCurrency = OWNERSHIP_CURRENCY_MAP[val] || '';
    setFormData({ ...formData, ownership: val, currency: autoCurrency });
  };

  const toggleInsurancePolicy = (policy) => {
    const current = formData.applicable_insurance_policies || [];
    if (current.includes(policy)) {
      setFormData({ ...formData, applicable_insurance_policies: current.filter(p => p !== policy) });
    } else {
      setFormData({ ...formData, applicable_insurance_policies: [...current, policy] });
    }
  };

  // Shared select class
  const selectCls = "w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm";

  return (
    <div className="p-6 md:p-8 lg:p-12" data-testid="equipment-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">Equipment Database</h1>
          <p className="text-sm text-slate-600 mt-2">Manage equipment inventory and details</p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button data-testid="equipment-audit-trail-button" variant="outline" className="border-slate-300 px-4 py-2 font-medium" onClick={() => setAuditOpen(true)}>
              <History className="w-4 h-4 mr-2" />Activity Log
            </Button>
            {/* Bulk Import Sheet */}
            <Sheet open={bulkImportOpen} onOpenChange={setBulkImportOpen}>
              <SheetTrigger asChild>
                <Button data-testid="bulk-import-button" variant="outline" className="border-slate-300 px-6 py-2 font-medium">
                  <Upload className="w-4 h-4 mr-2" />Bulk Import
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-xl lg:max-w-2xl overflow-y-auto" data-testid="bulk-import-sheet">
                <SheetHeader>
                  <SheetTitle className="text-2xl font-bold tracking-tight text-slate-900">Bulk Import Equipment</SheetTitle>
                  <SheetDescription>Upload CSV or Excel file to import multiple equipment items</SheetDescription>
                </SheetHeader>
                <div className="py-4 space-y-4">
                  <div className="bg-slate-50 p-4 rounded-sm border border-slate-200">
                    <h3 className="font-semibold text-sm text-slate-900 mb-2">Required Columns:</h3>
                    <ul className="text-xs text-slate-600 space-y-1"><li>serial_number</li><li>internal_tag</li><li>category</li><li>subcategory</li><li>maintenance_status</li><li>location</li></ul>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-sm border border-blue-200">
                    <h3 className="font-semibold text-sm text-slate-900 mb-2">Optional Columns Supported:</h3>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      <strong className="text-slate-700">General:</strong> equipment_name, aka, bundle_type, included_in, manufacturer, model, year_of_manufacture, capacity, alevro_tag, jv_code, quantity, assignment, next_maintenance_date<br/>
                      <strong className="text-slate-700">Dimensions &amp; Shipping:</strong> weight_kg, width_cm, length_cm, height_cm, unit_weight_kg, hs_code, customs_regime, customs_expiry_date, drawing_number, shipping_notes, dimensions<br/>
                      <strong className="text-slate-700">Finance:</strong> ownership, currency, asset_book, asset_book_code, acquisition_cost, acquisition_date, depreciation_method, useful_life_years, market_value, insured_value, applicable_insurance_policies, standard_daily_cost, release_quarter, intercompany_daily_rate, disposal_date, finance_notes, book_value, standard_rate<br/>
                      <strong className="text-slate-700">Sales:</strong> sales_notes
                    </p>
                    <p className="text-[11px] text-slate-500 mt-2 italic">
                      Lists like <code className="bg-white px-1 rounded">applicable_insurance_policies</code> accept "Item A | Item B" or "Item A, Item B". Booleans accept true/false/yes/no/1/0. Dates accept any standard date format.
                    </p>
                  </div>
                  <div><Button type="button" variant="outline" onClick={downloadTemplate} className="w-full"><Download className="w-4 h-4 mr-2" />Download CSV Template (full schema)</Button></div>
                  <div className="space-y-2">
                    <Label htmlFor="file-upload" className="text-sm font-semibold text-slate-700">Upload CSV or Excel File</Label>
                    <Input id="file-upload" type="file" accept=".csv,.xlsx,.xls" onChange={handleFileSelect} className="cursor-pointer" />
                    {selectedFile && <p className="text-xs text-slate-600">Selected: {selectedFile.name}</p>}
                  </div>
                </div>
                <SheetFooter>
                  <SheetClose asChild><Button variant="outline" disabled={uploading}>Cancel</Button></SheetClose>
                  <Button onClick={handleBulkImport} className="bg-slate-900 hover:bg-slate-800" disabled={!selectedFile || uploading}>{uploading ? 'Importing...' : 'Import'}</Button>
                </SheetFooter>
              </SheetContent>
            </Sheet>

            <Button onClick={() => { setEditingItem(null); resetForm(); setOpen(true); }} data-testid="add-equipment-button" className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm rounded-md px-6 py-2 font-medium">
              <Plus className="w-4 h-4 mr-2" />Add Equipment
            </Button>
          </div>
        )}
      </div>

      {/* Add/Edit Equipment Sheet - outside canEdit gate so row clicks open it */}
      <Sheet open={open} onOpenChange={(isOpen) => { setOpen(isOpen); if (!isOpen) resetForm(); }}>
        <SheetContent side="right" className="w-full sm:max-w-2xl lg:max-w-4xl overflow-y-auto" data-testid="equipment-sheet">
                <SheetHeader>
                  <SheetTitle className="text-2xl font-bold tracking-tight text-slate-900">{editingItem ? 'Edit Equipment' : 'Add Equipment'}</SheetTitle>
                  <SheetDescription>{editingItem ? 'Update equipment details below' : 'Add new equipment to your inventory'}</SheetDescription>
                </SheetHeader>

                <form id="equipment-form" onSubmit={handleSubmit}>
                <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
                  <TabsList className="grid w-full text-xs" style={{ gridTemplateColumns: `repeat(${[true, canView('equipment_database', 'dimensions'), canView('equipment_database', 'finance'), canView('equipment_database', 'sales'), canView('equipment_database', 'inspections')].filter(Boolean).length}, 1fr)` }}>
                    <TabsTrigger value="general" data-testid="general-tab">General</TabsTrigger>
                    {canView('equipment_database', 'dimensions') && <TabsTrigger value="dimensions" data-testid="dimensions-tab"><Ruler className="w-3 h-3 mr-1" />Dimensions</TabsTrigger>}
                    {canView('equipment_database', 'finance') && <TabsTrigger value="finance" data-testid="finance-tab"><DollarSign className="w-3 h-3 mr-1" />Finance</TabsTrigger>}
                    {canView('equipment_database', 'sales') && <TabsTrigger value="sales" data-testid="sales-tab"><Tag className="w-3 h-3 mr-1" />Sales</TabsTrigger>}
                    {canView('equipment_database', 'inspections') && <TabsTrigger value="inspections" data-testid="inspections-tab"><ClipboardCheck className="w-3 h-3 mr-1" />Inspections ({inspections.length})</TabsTrigger>}
                  </TabsList>

                  {/* ==================== GENERAL TAB ==================== */}
                  <TabsContent value="general">
                    <div className="py-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Category *</Label>
                          <select data-testid="category-select" value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value, subcategory: '' })} className={selectCls} required>
                            <option value="">Select Category</option>
                            {categories.map((cat) => (<option key={cat.id} value={cat.name}>{cat.name}</option>))}
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Subcategory *</Label>
                          <select data-testid="subcategory-select" value={formData.subcategory} onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })} className={selectCls} required disabled={!formData.category}>
                            <option value="">{formData.category ? 'Select Subcategory' : 'Select Category first'}</option>
                            {formData.category && subcategories.filter(s => s.parent_category === formData.category).map((s) => (<option key={s.id} value={s.name}>{s.name}</option>))}
                          </select>
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label className="text-sm font-semibold text-slate-700">Equipment Name / Description</Label>
                          <Input data-testid="equipment-name-input" value={formData.equipment_name} onChange={(e) => setFormData({ ...formData, equipment_name: e.target.value })} placeholder="e.g., 600T Crawler Crane" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">AKA</Label>
                          <Input data-testid="aka-input" value={formData.aka} onChange={(e) => setFormData({ ...formData, aka: e.target.value })} placeholder="Also known as (optional)" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Bundle Type</Label>
                          <select data-testid="bundle-type-select" value={formData.bundle_type} onChange={(e) => setFormData({ ...formData, bundle_type: e.target.value })} className={selectCls}>
                            <option value="">Select Bundle Type</option>
                            {bundleTypes.map(bt => <option key={bt} value={bt}>{bt}</option>)}
                          </select>
                        </div>
                        {formData.bundle_type && formData.bundle_type !== 'Loose' && (
                          <div className="space-y-2 md:col-span-2">
                            <Label className="text-sm font-semibold text-slate-700">Included In</Label>
                            <select data-testid="included-in-select" value={formData.included_in} onChange={(e) => {
                              const sel = includedInOptions.find(o => o.id === e.target.value);
                              setFormData({ ...formData, included_in: e.target.value, included_in_label: sel?.label || '' });
                            }} className={selectCls}>
                              <option value="">Select parent container / assembly...</option>
                              {includedInOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                            </select>
                            <p className="text-xs text-slate-500">Cross-reference to the container, box, or assembly this item lives in</p>
                          </div>
                        )}
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Manufacturer / Brand</Label>
                          <Input data-testid="manufacturer-input" value={formData.manufacturer} onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Model</Label>
                          <Input data-testid="model-input" value={formData.model} onChange={(e) => setFormData({ ...formData, model: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Year of Manufacture</Label>
                          <Input data-testid="year-input" type="number" min="1900" max="2099" value={formData.year_of_manufacture} onChange={(e) => setFormData({ ...formData, year_of_manufacture: e.target.value })} placeholder="e.g., 2020" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Tag Number *</Label>
                          <Input data-testid="internal-tag-input" value={formData.internal_tag} onChange={(e) => setFormData({ ...formData, internal_tag: e.target.value })} required />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Capacity</Label>
                          <Input data-testid="capacity-input" value={formData.capacity} onChange={(e) => setFormData({ ...formData, capacity: e.target.value })} placeholder="e.g., 600T" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Serial Number *</Label>
                          <Input data-testid="serial-number-input" value={formData.serial_number} onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })} required />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Fagioli Code</Label>
                          <Input data-testid="alevro-tag-input" value={formData.alevro_tag} onChange={(e) => setFormData({ ...formData, alevro_tag: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Joint Venture Code</Label>
                          <Input data-testid="jv-code-input" value={formData.jv_code} onChange={(e) => setFormData({ ...formData, jv_code: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Indivisible Quantity</Label>
                          <Input data-testid="quantity-input" type="number" min="1" value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Assignment</Label>
                          <select data-testid="assignment-select" value={formData.assignment} onChange={(e) => setFormData({ ...formData, assignment: e.target.value })} className={selectCls}>
                            <option value="">Select Assignment</option>
                            <option value="Depot">Depot</option>
                            {projects.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">{locationLabel} *</Label>
                          <select data-testid="location-select" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} className={selectCls} required>
                            <option value="">Select {locationLabel}</option>
                            {locations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Maintenance Status *</Label>
                          <select data-testid="maintenance-status-select" value={formData.maintenance_status} onChange={(e) => setFormData({ ...formData, maintenance_status: e.target.value })} className={selectCls} required>
                            <option value="Operational">Operational</option>
                            <option value="Maintenance">Maintenance</option>
                            <option value="Out of Service">Out of Service</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Next Maintenance Date</Label>
                          <Input data-testid="next-maintenance-date-input" type="date" value={formData.next_maintenance_date} onChange={(e) => setFormData({ ...formData, next_maintenance_date: e.target.value })} />
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  {/* ==================== DIMENSIONS & SHIPPING TAB ==================== */}
                  <TabsContent value="dimensions">
                    <div className="py-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Unit Weight [Kg]</Label>
                          <Input data-testid="unit-weight-input" type="number" step="0.01" value={formData.unit_weight_kg} onChange={(e) => setFormData({ ...formData, unit_weight_kg: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Width [cm]</Label>
                          <Input data-testid="width-input" type="number" step="0.01" value={formData.width_cm} onChange={(e) => setFormData({ ...formData, width_cm: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Length [cm]</Label>
                          <Input data-testid="length-input" type="number" step="0.01" value={formData.length_cm} onChange={(e) => setFormData({ ...formData, length_cm: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Height [cm]</Label>
                          <Input data-testid="height-input" type="number" step="0.01" value={formData.height_cm} onChange={(e) => setFormData({ ...formData, height_cm: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Volume [m3]</Label>
                          <div className="w-full h-10 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm flex items-center font-mono" data-testid="volume-display">
                            {calcVolume || '-'}
                          </div>
                          <p className="text-xs text-slate-500">Auto-calculated from W x L x H</p>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Total Weight [Kg]</Label>
                          <div className="w-full h-10 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm flex items-center font-mono" data-testid="total-weight-display">
                            {calcTotalWeight || '-'}
                          </div>
                          <p className="text-xs text-slate-500">Unit Weight x Indivisible Quantity</p>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Freight Tons</Label>
                          <div className="w-full h-10 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm flex items-center font-mono" data-testid="freight-tons-display">
                            {calcFreightTons || '-'}
                          </div>
                          <p className="text-xs text-slate-500">max(Total Weight / 1000, Volume)</p>
                        </div>

                        <div className="md:col-span-2 border-t border-slate-200 pt-4 mt-2">
                          <h3 className="text-sm font-semibold text-slate-700 mb-3">Customs & Documentation</h3>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">HS Code</Label>
                          <Input data-testid="hs-code-input" value={formData.hs_code} onChange={(e) => setFormData({ ...formData, hs_code: e.target.value })} placeholder="Harmonized System code" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Customs Regime</Label>
                          <select data-testid="customs-regime-select" value={formData.customs_regime} onChange={(e) => setFormData({ ...formData, customs_regime: e.target.value })} className={selectCls}>
                            <option value="">Select Regime</option>
                            <option value="Permanent">Permanent</option>
                            <option value="Temporary">Temporary</option>
                          </select>
                        </div>
                        {formData.customs_regime === 'Temporary' && (
                          <div className="space-y-2">
                            <Label className="text-sm font-semibold text-slate-700">Temporary Import Expiry Date</Label>
                            <Input data-testid="customs-expiry-input" type="date" value={formData.customs_expiry_date} onChange={(e) => setFormData({ ...formData, customs_expiry_date: e.target.value })} />
                          </div>
                        )}
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Drawing Number</Label>
                          <Input data-testid="drawing-number-input" value={formData.drawing_number} onChange={(e) => setFormData({ ...formData, drawing_number: e.target.value })} />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label className="text-sm font-semibold text-slate-700">Notes / Remarks</Label>
                          <Textarea data-testid="shipping-notes-input" value={formData.shipping_notes} onChange={(e) => setFormData({ ...formData, shipping_notes: e.target.value })} rows={3} placeholder="Additional shipping info..." />
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  {/* ==================== FINANCE & ACCOUNTING TAB ==================== */}
                  <TabsContent value="finance">
                    <div className="py-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Ownership</Label>
                          <select data-testid="ownership-select" value={formData.ownership} onChange={(e) => handleOwnershipChange(e.target.value)} className={selectCls}>
                            <option value="">Select Entity</option>
                            {ownershipEntities.map(e => <option key={e} value={e}>{e}</option>)}
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Currency</Label>
                          <select data-testid="currency-select" value={formData.currency} onChange={(e) => setFormData({ ...formData, currency: e.target.value })} className={selectCls}>
                            <option value="">Select Currency</option>
                            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                          <p className="text-xs text-slate-500">Auto-set from ownership, adjustable manually</p>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Asset Book</Label>
                          <div className="flex items-center gap-3 h-10">
                            <Switch data-testid="asset-book-toggle" checked={formData.asset_book} onCheckedChange={(v) => setFormData({ ...formData, asset_book: v })} />
                            <span className="text-sm text-slate-600">{formData.asset_book ? 'Yes' : 'No'}</span>
                          </div>
                        </div>
                        {formData.asset_book && (
                          <div className="space-y-2">
                            <Label className="text-sm font-semibold text-slate-700">Asset Book Code</Label>
                            <Input data-testid="asset-book-code-input" value={formData.asset_book_code} onChange={(e) => setFormData({ ...formData, asset_book_code: e.target.value })} />
                          </div>
                        )}

                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Acquisition Cost</Label>
                          <Input data-testid="acquisition-cost-input" type="number" step="0.01" value={formData.acquisition_cost} onChange={(e) => setFormData({ ...formData, acquisition_cost: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Acquisition Date</Label>
                          <Input data-testid="acquisition-date-input" type="date" value={formData.acquisition_date} onChange={(e) => setFormData({ ...formData, acquisition_date: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Depreciation Method</Label>
                          <select data-testid="depreciation-method-select" value={formData.depreciation_method} onChange={(e) => setFormData({ ...formData, depreciation_method: e.target.value })} className={selectCls}>
                            <option value="">Select Method</option>
                            {DEPRECIATION_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Useful Life (Years)</Label>
                          <Input data-testid="useful-life-input" type="number" min="1" value={formData.useful_life_years} onChange={(e) => setFormData({ ...formData, useful_life_years: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">YTD Depreciation</Label>
                          <div className="w-full h-10 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm flex items-center font-mono" data-testid="ytd-depreciation-display">
                            {calcYtdDepreciation || '-'}
                          </div>
                          <p className="text-xs text-slate-500">Straight-line auto-calc</p>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Net Book Value</Label>
                          <div className="w-full h-10 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm flex items-center font-mono" data-testid="net-book-value-display">
                            {calcNetBookValue || '-'}
                          </div>
                          <p className="text-xs text-slate-500">Acquisition Cost - Accumulated Depreciation</p>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Market Value</Label>
                          <Input data-testid="market-value-input" type="number" step="0.01" value={formData.market_value} onChange={(e) => setFormData({ ...formData, market_value: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Insured Value</Label>
                          <Input data-testid="insured-value-input" type="number" step="0.01" value={formData.insured_value} onChange={(e) => setFormData({ ...formData, insured_value: e.target.value })} />
                        </div>

                        <div className="md:col-span-2 space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Applicable Insurance Policies</Label>
                          <div className="flex flex-wrap gap-2" data-testid="insurance-policies-select">
                            {INSURANCE_POLICY_TYPES.map(policy => (
                              <button
                                key={policy} type="button"
                                onClick={() => toggleInsurancePolicy(policy)}
                                className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                                  (formData.applicable_insurance_policies || []).includes(policy)
                                    ? 'bg-slate-900 text-white border-slate-900'
                                    : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                                }`}
                              >{policy}</button>
                            ))}
                          </div>
                        </div>

                        <div className="md:col-span-2 border-t border-slate-200 pt-4 mt-2">
                          <h3 className="text-sm font-semibold text-slate-700 mb-3">Rates & Disposal</h3>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Standard Daily Cost</Label>
                          <Input data-testid="standard-daily-cost-input" type="number" step="0.01" value={formData.standard_daily_cost} onChange={(e) => setFormData({ ...formData, standard_daily_cost: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Release Quarter</Label>
                          <Input data-testid="release-quarter-input" value={formData.release_quarter} onChange={(e) => setFormData({ ...formData, release_quarter: e.target.value })} placeholder="e.g., Q3-2025" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Intercompany Daily Rate</Label>
                          <Input data-testid="intercompany-rate-input" type="number" step="0.01" value={formData.intercompany_daily_rate} onChange={(e) => setFormData({ ...formData, intercompany_daily_rate: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Disposal Date</Label>
                          <Input data-testid="disposal-date-input" type="date" value={formData.disposal_date} onChange={(e) => setFormData({ ...formData, disposal_date: e.target.value })} />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label className="text-sm font-semibold text-slate-700">Notes / Remarks</Label>
                          <Textarea data-testid="finance-notes-input" value={formData.finance_notes} onChange={(e) => setFormData({ ...formData, finance_notes: e.target.value })} rows={3} placeholder="Additional finance info..." />
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  {/* ==================== SALES & TENDERING TAB ==================== */}
                  <TabsContent value="sales">
                    <div className="py-4">
                      <h3 className="text-sm font-semibold text-slate-700 mb-4">Suggested Min Price Daily Rates</h3>
                      <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="pricing-matrix">
                        <table className="w-full">
                          <thead>
                            <tr className="bg-slate-100">
                              <th className="text-left text-xs font-semibold text-slate-700 px-4 py-3 w-[30%] border-r border-slate-200">Region</th>
                              <th className="text-center text-xs font-semibold text-slate-700 px-4 py-3 border-r border-slate-200">Short</th>
                              <th className="text-center text-xs font-semibold text-slate-700 px-4 py-3 border-r border-slate-200">Mid</th>
                              <th className="text-center text-xs font-semibold text-slate-700 px-4 py-3">Long</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[
                              { label: 'EMEA', prefix: 'emea' },
                              { label: 'APAC', prefix: 'apac' },
                              { label: 'NORTH AMERICA', prefix: 'na' }
                            ].map(({ label, prefix }) => (
                              <tr key={prefix} className="border-t border-slate-200">
                                <td className="text-sm font-medium text-slate-700 px-4 py-3 border-r border-slate-200 bg-slate-50">{label}</td>
                                {['short', 'mid', 'long'].map(dur => (
                                  <td key={dur} className="px-2 py-2 border-r border-slate-200 last:border-r-0">
                                    <Input
                                      type="number"
                                      step="0.01"
                                      data-testid={`rate-${prefix}-${dur}`}
                                      value={(formData.pricing_matrix || {})[`${prefix}_${dur}`] || ''}
                                      onChange={(e) => setFormData({
                                        ...formData,
                                        pricing_matrix: { ...formData.pricing_matrix, [`${prefix}_${dur}`]: e.target.value }
                                      })}
                                      className="text-center h-9"
                                      placeholder="-"
                                    />
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <p className="text-xs text-slate-500 mt-2">Daily rates by region and project duration</p>
                      <div className="space-y-2 mt-6">
                        <Label className="text-sm font-semibold text-slate-700">Notes / Remarks / Additional Info</Label>
                        <Textarea data-testid="sales-notes-input" value={formData.sales_notes} onChange={(e) => setFormData({ ...formData, sales_notes: e.target.value })} rows={4} placeholder="Additional sales and tendering information..." />
                      </div>
                    </div>
                  </TabsContent>

                  {/* ==================== INSPECTIONS TAB ==================== */}
                  <TabsContent value="inspections">
                    <div className="py-4">
                      <div className="flex justify-between items-center mb-4">
                        <p className="text-sm text-slate-600">Manage equipment inspections and tests</p>
                        <Button type="button" size="sm" onClick={handleAddInsp} data-testid="add-inspection-button" className="bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4 mr-1" />Add Inspection</Button>
                      </div>
                      {showInspForm && (
                        <Card className="mb-4 border-blue-200 bg-blue-50">
                          <CardContent className="pt-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div className="space-y-1"><Label className="text-xs font-semibold">Type *</Label><select value={inspFormData.type} onChange={(e) => setInspFormData({ ...inspFormData, type: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-sm" data-testid="insp-type-select"><option value="">Select Type</option><option value="Load Test">Load Test</option><option value="NDT">NDT</option><option value="Third Party">Third Party</option><option value="Annual Inspection">Annual Inspection</option><option value="Calibration">Calibration</option><option value="Visual">Visual</option><option value="Other">Other</option></select></div>
                              <div className="space-y-1"><Label className="text-xs font-semibold">Name *</Label><Input value={inspFormData.name} onChange={(e) => setInspFormData({ ...inspFormData, name: e.target.value })} placeholder="e.g., Annual Load Test" data-testid="insp-name-input" /></div>
                              <div className="space-y-1"><Label className="text-xs font-semibold">Inspector</Label><Input value={inspFormData.inspector} onChange={(e) => setInspFormData({ ...inspFormData, inspector: e.target.value })} placeholder="Inspector/Company" /></div>
                              <div className="space-y-1"><Label className="text-xs font-semibold">Certificate #</Label><Input value={inspFormData.certificate_number} onChange={(e) => setInspFormData({ ...inspFormData, certificate_number: e.target.value })} placeholder="Certificate number" /></div>
                              <div className="space-y-1"><Label className="text-xs font-semibold">Inspection Date</Label><Input type="date" value={inspFormData.inspection_date} onChange={(e) => setInspFormData({ ...inspFormData, inspection_date: e.target.value })} /></div>
                              <div className="space-y-1"><Label className="text-xs font-semibold">Next Due Date</Label><Input type="date" value={inspFormData.next_due_date} onChange={(e) => setInspFormData({ ...inspFormData, next_due_date: e.target.value })} /></div>
                              <div className="space-y-1"><Label className="text-xs font-semibold">Result</Label><select value={inspFormData.result} onChange={(e) => setInspFormData({ ...inspFormData, result: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-sm"><option value="Pass">Pass</option><option value="Fail">Fail</option><option value="Conditional">Conditional</option></select></div>
                              <div className="space-y-1"><Label className="text-xs font-semibold">SWL Verified</Label><Input value={inspFormData.swl_verified} onChange={(e) => setInspFormData({ ...inspFormData, swl_verified: e.target.value })} placeholder="Safe Working Load" /></div>
                              <div className="space-y-1 md:col-span-2"><Label className="text-xs font-semibold">Notes</Label><Input value={inspFormData.notes} onChange={(e) => setInspFormData({ ...inspFormData, notes: e.target.value })} /></div>
                            </div>
                            <div className="flex justify-end gap-2 mt-3">
                              <Button type="button" variant="outline" size="sm" onClick={() => setShowInspForm(false)}>Cancel</Button>
                              <Button type="button" size="sm" onClick={handleSaveInsp} className="bg-blue-500 hover:bg-blue-600" data-testid="save-inspection-button">{editingInsp ? 'Update' : 'Add'}</Button>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                      {inspections.length === 0 ? (<div className="text-center py-8 text-slate-500"><ClipboardCheck className="w-10 h-10 mx-auto mb-2 text-slate-300" /><p>No inspections recorded</p></div>) : (
                        <Table><TableHeader><TableRow><TableHead className="text-xs">Type</TableHead><TableHead className="text-xs">Name</TableHead><TableHead className="text-xs">Inspector</TableHead><TableHead className="text-xs">Next Due</TableHead><TableHead className="text-xs">Result</TableHead><TableHead className="text-xs w-[80px]">Actions</TableHead></TableRow></TableHeader><TableBody>
                          {inspections.map((insp) => (
                            <TableRow key={insp.id}><TableCell className="text-sm">{insp.type}</TableCell><TableCell className="text-sm">{insp.name}</TableCell><TableCell className="text-sm">{insp.inspector || '-'}</TableCell><TableCell className="text-sm">{formatDisplayDate(insp.next_due_date) || '-'}</TableCell><TableCell><Badge className={getStatusBadge(insp.result)}>{insp.result}</Badge></TableCell><TableCell><div className="flex gap-1"><Button type="button" variant="ghost" size="sm" onClick={() => handleEditInsp(insp)} className="h-8 w-8 p-0"><Pencil className="w-3 h-3" /></Button><Button type="button" variant="ghost" size="sm" onClick={() => handleDeleteInsp(insp.id)} className="h-8 w-8 p-0 text-red-600"><Trash2 className="w-3 h-3" /></Button></div></TableCell></TableRow>
                          ))}
                        </TableBody></Table>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
                </form>

                <SheetFooter className="mt-4">
                  <SheetClose asChild><Button type="button" variant="outline" data-testid="cancel-button">Cancel</Button></SheetClose>
                  <Button type="submit" form="equipment-form" data-testid="save-equipment-button" className="bg-slate-900 hover:bg-slate-800">{editingItem ? 'Update' : 'Create'}</Button>
                </SheetFooter>
              </SheetContent>
      </Sheet>

      <Card className="bg-white border-slate-200 shadow-sm" data-testid="equipment-table">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CardTitle className="text-lg font-semibold tracking-tight text-slate-700 uppercase">
                {showTrash ? 'Trash' : 'Equipment List'}
              </CardTitle>
              {canEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setShowTrash(!showTrash); setSelectedIds(new Set()); }}
                  data-testid="equipment-toggle-trash-button"
                  className={showTrash ? "border-slate-300 text-slate-700 font-medium" : "border-slate-300 text-slate-600"}
                >
                  {showTrash ? (
                    <><ArrowLeft className="w-3.5 h-3.5 mr-1.5" />Back to Equipment List</>
                  ) : (
                    <><Trash2 className="w-3.5 h-3.5 mr-1.5" />Trash ({trashedEquipment.length})</>
                  )}
                </Button>
              )}
            </div>
            <span className="text-xs text-slate-500" data-testid="equipment-count">
              {showTrash 
                ? `${trashedEquipment.length} in trash`
                : `Showing ${equipment.length} of ${totalCount} items`}
            </span>
          </div>
          {/* Bulk action bar */}
          {canEdit && !showTrash && selectedIds.size > 0 && (
            <div className="mt-3 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2" data-testid="equipment-bulk-action-bar">
              <span className="text-sm font-medium text-amber-800">{selectedIds.size} selected</span>
              <Button size="sm" variant="destructive" onClick={handleBulkDelete} data-testid="equipment-bulk-delete-button" className="h-7">
                <Trash2 className="w-3.5 h-3.5 mr-1" />Move to Trash
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} className="h-7 text-slate-600">
                Clear Selection
              </Button>
            </div>
          )}
          {showTrash && trashedEquipment.length > 0 && canEdit && (
            <div className="mt-3 flex items-center gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <span className="text-sm text-red-700">Items in trash will remain until permanently deleted.</span>
              <Button size="sm" variant="destructive" onClick={handleEmptyTrash} data-testid="equipment-empty-trash-button" className="h-7 ml-auto">
                Empty Trash
              </Button>
            </div>
          )}
          {!showTrash && (
            <div className="mt-3 space-y-3">
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    data-testid="equipment-search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by Tag, Name, or Serial Number..."
                    className="pl-10 pr-10"
                  />
                  {searchQuery && (
                    <button type="button" onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <TableViewToolbar {...tableView} columns={tableColumns} />
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {showTrash ? (
            /* Trash view */
            trashedEquipment.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Trash2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">Trash is empty</p>
                <p className="text-xs mt-1">Deleted equipment will appear here</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-mono text-xs uppercase">Tag</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Name / S/N</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Category</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Deleted By</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Deleted At</TableHead>
                    <TableHead className="font-mono text-xs uppercase w-[160px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {trashedEquipment.map((item) => (
                    <TableRow key={item.id} className="opacity-70" data-testid={`equipment-trash-row-${item.id}`}>
                      <TableCell className="font-mono text-sm">{item.internal_tag}</TableCell>
                      <TableCell className="text-sm">
                        <div>{item.equipment_name || item.serial_number}</div>
                        {item.equipment_name && <div className="text-xs text-slate-500">{item.serial_number}</div>}
                      </TableCell>
                      <TableCell className="text-sm">{item.category}{item.subcategory ? ` / ${item.subcategory}` : ''}</TableCell>
                      <TableCell className="text-sm text-slate-500">{item.deleted_by || '-'}</TableCell>
                      <TableCell className="text-sm text-slate-500">{item.deleted_at ? formatDisplayDate(item.deleted_at) : '-'}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => handleRestore(item.id)} data-testid={`equipment-restore-${item.id}`} className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                            <RotateCcw className="w-3 h-3 mr-1" />Restore
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handlePermanentDelete(item.id)} data-testid={`equipment-perm-delete-${item.id}`} className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" title="Delete permanently">
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          ) : (
          <Table>
            <TableHeader>
              <TableRow>
                {canEdit && (
                  <TableHead className="w-[40px]">
                    <input
                      type="checkbox"
                      data-testid="equipment-select-all"
                      checked={filteredEquipment.length > 0 && selectedIds.size === filteredEquipment.length}
                      ref={el => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < filteredEquipment.length; }}
                      onChange={toggleSelectAll}
                      className="rounded border-slate-300 cursor-pointer"
                    />
                  </TableHead>
                )}
                {tableView.isColumnVisible('internal_tag') && <SortableHead field="internal_tag" label="Tag" sortBy={tableView.sortBy} sortDir={tableView.sortDir} onSort={tableView.toggleSort} />}
                {tableView.isColumnVisible('name_sn') && <SortableHead field="equipment_name" label="Name / S/N" sortBy={tableView.sortBy} sortDir={tableView.sortDir} onSort={tableView.toggleSort} />}
                {tableView.isColumnVisible('category') && <SortableHead field="category" label="Category" sortBy={tableView.sortBy} sortDir={tableView.sortDir} onSort={tableView.toggleSort} />}
                {tableView.isColumnVisible('subcategory') && <SortableHead field="subcategory" label="Subcategory" sortBy={tableView.sortBy} sortDir={tableView.sortDir} onSort={tableView.toggleSort} />}
                {tableView.isColumnVisible('maintenance_status') && <SortableHead field="maintenance_status" label="Status" sortBy={tableView.sortBy} sortDir={tableView.sortDir} onSort={tableView.toggleSort} />}
                {tableView.isColumnVisible('location') && <SortableHead field="location" label={locationLabel} sortBy={tableView.sortBy} sortDir={tableView.sortDir} onSort={tableView.toggleSort} />}
                {tableView.isColumnVisible('ownership') && <SortableHead field="ownership" label="Owner" sortBy={tableView.sortBy} sortDir={tableView.sortDir} onSort={tableView.toggleSort} />}
                {tableView.isColumnVisible('bundle_type') && <TableHead className="font-mono text-xs uppercase">Bundle Type</TableHead>}
                {tableView.isColumnVisible('assignment') && <SortableHead field="assignment" label="Assignment" sortBy={tableView.sortBy} sortDir={tableView.sortDir} onSort={tableView.toggleSort} />}
                {tableView.isColumnVisible('modified_by') && <TableHead className="font-mono text-xs uppercase">Modified By</TableHead>}
                {canEdit && <TableHead className="font-mono text-xs uppercase w-[60px]">Actions</TableHead>}
              </TableRow>
              <ColumnFilterRow columns={tableColumns} columnFilters={tableView.columnFilters} setColumnFilter={tableView.setColumnFilter} isColumnVisible={tableView.isColumnVisible} leadingCells={canEdit ? 1 : 0} />
            </TableHeader>
            <TableBody>
              {filteredEquipment.map((item) => (
                <TableRow key={item.id} data-testid={`equipment-row-${item.id}`} className={`cursor-pointer hover:bg-slate-50 ${selectedIds.has(item.id) ? 'bg-amber-50/50' : ''}`} onClick={(e) => handleRowClick(item, e)}>
                  {canEdit && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        data-testid={`equipment-select-${item.id}`}
                        checked={selectedIds.has(item.id)}
                        onChange={() => toggleSelect(item.id)}
                        className="rounded border-slate-300 cursor-pointer"
                      />
                    </TableCell>
                  )}
                  {tableView.isColumnVisible('internal_tag') && <TableCell className="font-mono text-sm">{item.internal_tag}</TableCell>}
                  {tableView.isColumnVisible('name_sn') && (
                    <TableCell className="text-sm">
                      <div>{item.equipment_name || item.serial_number}</div>
                      {item.equipment_name && <div className="text-xs text-slate-500">{item.serial_number}</div>}
                    </TableCell>
                  )}
                  {tableView.isColumnVisible('category') && <TableCell className="text-sm">{item.category}</TableCell>}
                  {tableView.isColumnVisible('subcategory') && <TableCell className="text-sm">{item.subcategory}</TableCell>}
                  {tableView.isColumnVisible('maintenance_status') && <TableCell><Badge className={getStatusBadge(item.maintenance_status)}>{item.maintenance_status}</Badge></TableCell>}
                  {tableView.isColumnVisible('location') && <TableCell className="text-sm">{item.location}</TableCell>}
                  {tableView.isColumnVisible('ownership') && <TableCell className="text-sm">{item.ownership || '-'}</TableCell>}
                  {tableView.isColumnVisible('bundle_type') && <TableCell className="text-sm">{item.bundle_type || '-'}</TableCell>}
                  {tableView.isColumnVisible('assignment') && <TableCell className="text-sm">{item.assignment || '-'}</TableCell>}
                  {tableView.isColumnVisible('modified_by') && <TableCell><LastModifiedBadge createdBy={item.created_by} updatedBy={item.updated_by} updatedAt={item.updated_at} /></TableCell>}
                  {canEdit && (
                    <TableCell>
                      <div data-actions-menu>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`actions-menu-${item.id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEdit(item)} data-testid={`edit-equipment-${item.id}`}><Pencil className="mr-2 h-4 w-4" />Edit</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(item.id)} data-testid={`delete-equipment-${item.id}`} className="text-red-600 focus:text-red-600"><Trash2 className="mr-2 h-4 w-4" />Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          )}
          {!showTrash && !listLoading && equipment.length === 0 && hasActiveFilters && (
            <div className="text-center py-8 text-slate-500">
              <Search className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">No equipment matches your search or filters</p>
              <button type="button" onClick={clearFilters} className="text-xs mt-2 text-amber-600 hover:text-amber-700 font-medium underline underline-offset-2">Clear filters</button>
            </div>
          )}
          {!showTrash && !listLoading && equipment.length === 0 && !hasActiveFilters && (
            <p className="text-center text-slate-500 py-8 text-sm">No equipment found</p>
          )}
          {!showTrash && listLoading && equipment.length === 0 && (
            <p className="text-center text-slate-400 py-8 text-sm">Loading equipment…</p>
          )}
          {/* Load more / infinite-scroll sentinel */}
          {!showTrash && hasMore && (
            <div ref={sentinelRef} className="flex justify-center py-4">
              <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore} data-testid="equipment-load-more">
                {loadingMore ? 'Loading…' : `Load more (${equipment.length} of ${totalCount})`}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      <AuditTrailPanel open={auditOpen} onOpenChange={setAuditOpen} entityType="equipment" title="Equipment Activity Log" />
    </div>
  );
}
